'use client';

import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { createProductUrl } from '@/lib/product-utils';
import { cn } from '@/lib/utils';
import { SvgCartArrow, SvgRight } from '../icons-eataly';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useCommerceFeedback } from '@/providers/commerce-feedback-context';
import { useBasketUpdater } from '@/providers/basket';
import { useAnalytics } from '@/hooks/use-analytics';
import { buildDataLayerItemSource } from '@/lib/analytics/data-layer-mappers';
import { getPlpAddToCartProductId, getPlpProductToTrack, isPlpSearchHitBundleOrSet } from '@/lib/cart/plp-basket-line';
import {
    filterApproachingPromotionsForSearchHit,
    filterEditorialPromotionsForSearchHit,
} from '@/lib/approaching-promotions-filter';
import type { ShopperBasketsV2, ShopperSearch } from '@/scapi';
import type { BasketActionResponse } from '@/routes/types/action-responses';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';

export type ProductTileCustomQuickAddDisplay = 'default' | 'icon' | 'tag';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];

/** Sold-out control keeps the sand icon button. The card link is the only PDP target. */
function SoldOutProductButton({ className, label }: { className?: string; label: string }) {
    return (
        <Button
            type="button"
            hierarchy="primary"
            mode="dark"
            size="icon-small"
            iconOnly
            aria-label={label}
            tabIndex={-1}
            aria-hidden
            className={cn('pointer-events-none relative z-20 shrink-0', className)}>
            <SvgRight className="size-5" aria-hidden />
        </Button>
    );
}

interface ProductTileCustomQuickAddButtonProps {
    productUrl: string;
    product: SearchHit;
    productName: string;
    selectedColorValue?: string | null;
    display?: ProductTileCustomQuickAddDisplay;
    className?: string;
    disabled?: boolean;
}

/**
 * Add-to-cart for ProductTileCustom: adds one line item via `/action/cart-item-add` (no modal).
 * Product-set hits navigate to the PDP; bundles and single-SKU / variant resolution use the same
 * rules as the cart line matcher (bundles ATC via parent id).
 */
export function ProductTileCustomQuickAddButton({
    product,
    productName,
    selectedColorValue,
    display = 'default',
    className,
    disabled = false,
}: ProductTileCustomQuickAddButtonProps) {
    const { t } = useTranslation('product');
    const { showAddedToCart, showFailedToAddCart, showBasketQuotaExceeded } = useCommerceFeedback();
    const updateBasket = useBasketUpdater();
    const navigate = useNavigate();
    const analytics = useAnalytics();

    const itemProductId = getPlpAddToCartProductId(product, selectedColorValue);
    const fetcher = useItemFetcher({
        itemId: product.productId ?? 'plp',
        componentName: 'product-tile-custom-quick-add',
    });
    const [isAdding, setIsAdding] = useState(false);
    const pendingAddRef = useRef(0);
    const lastSubmittedQtyRef = useRef<number | null>(null);
    const debounceTimerRef = useRef<number | null>(null);

    const resolvedLabel = t('quickAdd');
    const viewProductLabel = t('viewProductAriaLabel', { productName });
    const tagLabel = t('tileAddToCart');
    const isSubmitting = fetcher.state === 'submitting';
    const isBundleOrSet = isPlpSearchHitBundleOrSet(product);
    const isDisabled = disabled;
    const isNotOrderable = !product.orderable;

    const flushPending = useCallback(
        (qtyOverride?: number) => {
            if (isDisabled || isSubmitting) return;
            if (!itemProductId) return;
            const qty = qtyOverride ?? pendingAddRef.current;
            if (!qty || qty < 1) return;

            pendingAddRef.current = 0;
            lastSubmittedQtyRef.current = qty;
            setIsAdding(true);

            const productItem = { productId: itemProductId, quantity: qty };
            void fetcher.submit(
                { productItem: JSON.stringify(productItem) },
                { method: 'POST', action: '/action/cart-item-add' }
            );
        },
        [fetcher, isDisabled, isSubmitting, itemProductId]
    );

    useEffect(() => {
        if (!isAdding || fetcher.state !== 'idle' || !fetcher.data) {
            return;
        }
        setIsAdding(false);
        const data = fetcher.data as BasketActionResponse & { availableQuantity?: number };
        const submittedQty = lastSubmittedQtyRef.current ?? 1;
        lastSubmittedQtyRef.current = null;
        if (data.success && data.basket) {
            updateBasket(data.basket);
            const basket = data.basket as SCAPIShopperBasketsV2Schemas['Basket'];
            const approachingPromotions = basket?.c_approachingPromotions;
            const editorialPromotions = basket?.c_editorialPromotions;

            showAddedToCart({
                productName,
                quantity: submittedQty,
                approachingPromotions: filterApproachingPromotionsForSearchHit(
                    approachingPromotions,
                    product,
                    itemProductId
                ),
                editorialPromotions: filterEditorialPromotionsForSearchHit(editorialPromotions, product, itemProductId),
            });
            const basketLine = data.basket.productItems?.find((item) => item.productId === itemProductId);
            // Keep the basket line untouched so its own quantity de-totalizes the unit price,
            // and report the amount added this event via `eventQuantity`.
            const cartItem = buildDataLayerItemSource(
                basketLine ?? { productId: itemProductId, quantity: submittedQty },
                getPlpProductToTrack(product, selectedColorValue, submittedQty)
            );
            void analytics.trackCartItemAdd({
                cartItems: [cartItem as ShopperBasketsV2.schemas['ProductItem']],
                eventQuantity: submittedQty,
            });
        } else if (data.success === false) {
            if (data.isBasketQuotaExceeded) {
                showBasketQuotaExceeded();
                return;
            }
            // If SCAPI told us the max available quantity, try adding that instead (best effort).
            const availableQty = data.availableQuantity;
            if (typeof availableQty === 'number' && Number.isFinite(availableQty) && availableQty > 0) {
                showFailedToAddCart();
                // Try to add as many as we can, capped by what the user asked for.
                const fallbackQty = Math.min(availableQty, submittedQty);
                if (fallbackQty > 0) {
                    flushPending(fallbackQty);
                }
            } else {
                showFailedToAddCart();
            }
        }
    }, [
        showAddedToCart,
        analytics,
        fetcher.data,
        fetcher.state,
        flushPending,
        isAdding,
        itemProductId,
        product,
        productName,
        selectedColorValue,
        showFailedToAddCart,
        showBasketQuotaExceeded,
        updateBasket,
    ]);

    // If user kept clicking while the request was in-flight, flush the queued adds when we're idle again.
    useEffect(() => {
        if (fetcher.state !== 'idle') return;
        if (pendingAddRef.current < 1) return;
        if (debounceTimerRef.current != null) return;
        flushPending();
    }, [fetcher.state, flushPending]);

    const addOneToCart = useCallback(
        (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (isDisabled) {
                return;
            }
            if (isBundleOrSet) {
                void navigate(createProductUrl(product.productId ?? '', selectedColorValue ?? null, 'color'));
                return;
            }
            if (!itemProductId) {
                showFailedToAddCart();
                return;
            }
            pendingAddRef.current += 1;
            if (debounceTimerRef.current != null) {
                window.clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = window.setTimeout(() => {
                debounceTimerRef.current = null;
                flushPending();
            }, 250);
        },
        [
            isDisabled,
            isBundleOrSet,
            itemProductId,
            navigate,
            product.productId,
            selectedColorValue,
            flushPending,
            showFailedToAddCart,
        ]
    );

    return (
        <>
            {display === 'icon' &&
                (isNotOrderable ? (
                    <SoldOutProductButton className={className} label={viewProductLabel} />
                ) : (
                    <Button
                        type="button"
                        hierarchy="primary"
                        size="icon-small"
                        iconOnly={true}
                        aria-label={`${resolvedLabel} ${productName}`}
                        onClick={addOneToCart}
                        className={cn('pointer-events-auto relative z-20 cursor-pointer', className)}
                        disabled={isDisabled || isSubmitting}>
                        <SvgCartArrow className="size-5" aria-hidden />
                    </Button>
                ))}

            {display === 'tag' &&
                (isNotOrderable ? (
                    <SoldOutProductButton className={className} label={viewProductLabel} />
                ) : (
                    <Button
                        type="button"
                        hierarchy="primary"
                        aria-label={`${tagLabel} ${productName}`}
                        iconAfter={<SvgCartArrow className="size-5" aria-hidden />}
                        onClick={addOneToCart}
                        size="small"
                        className={cn('pointer-events-auto cursor-pointer', className)}
                        disabled={isDisabled || isSubmitting}>
                        <span className="body-copy-m-bold">{tagLabel}</span>
                    </Button>
                ))}
        </>
    );
}
